/**
 * Wire types for the skill-service surfaces the admin binds to. These mirror the
 * server contracts:
 *   - ROLE-ADMIN-API.md   (TRE-783): /account/context, /account/members, POST role
 *   - admin/README.md     (TRE-780): /admin/trash, /admin/locks, force-release,
 *                                     hard-delete, restore
 *   - entitlement.ts      (TRE-781): GET /entitlement gate
 *   - auth/routes.ts       (TRE-779/247): /auth/config, /auth/email/*
 * The client trusts these shapes but treats every server *response* as truth
 * (thin-client contract §9) — capabilities gate the UI, the server enforces.
 */

export const ROLES = ["runner", "creator", "admin", "owner"] as const;
export type Role = (typeof ROLES)[number];

export type Capability =
  | "run_skill"
  | "author_skill"
  | "manage_trash"
  | "force_release_lock"
  | "assign_role"
  | "manage_billing";

export type Tier = "base" | "creator";

export interface Identity {
  sub: string;
  email: string;
  account: string;
  role: Role;
  idp?: string;
}

/** GET /account/context — the shell landing + capability-gated nav. */
export interface AccountContext {
  identity: Identity;
  account: string;
  capabilities: Capability[];
  assignableRoles: Role[];
  /**
   * Whether the caller may see the cross-tenant dev-handoff viewer (TRE-987). NOT a
   * per-account capability — the server gates it on the Treillage maintainer allow-list
   * — so it rides as its own flag and drives that one tab's visibility. Absent/false ⇒
   * a client firm's admin sees no tab (and the API 403s), so no cross-tenant leak.
   */
  handoffMaintainer?: boolean;
}

/** A row of GET /account/members. */
export interface Member {
  sub: string;
  email: string;
  joinedAt: string;
  role: Role;
  isSelf: boolean;
}

export interface MembersResponse {
  count: number;
  members: Member[];
  assignableRoles: Role[];
  ownerCount: number;
}

/** A pre-assignment (invite) — a role designated for an email before first sign-in (TRE-850). */
export interface PendingAssignment {
  email: string;
  role: Role;
  invitedBy: string;
  invitedAt: string;
  /**
   * True when the invited email is outside the firm's domain — a cross-domain
   * guest (TRE-851). On first sign-in the guest joins THIS account rather than
   * provisioning their own. Absent/false = a same-domain pre-assignment.
   */
  guest?: boolean;
}

/** GET /account/pending — active pre-assignments for the caller's account. */
export interface PendingResponse {
  count: number;
  pending: PendingAssignment[];
  assignableRoles: Role[];
}

/**
 * An active invite *link* (TRE-1044) — a shareable, expiring URL that binds whoever
 * redeems it to the account at `role`. Only the non-secret `id` (list/revoke
 * address), role, and expiry are ever returned; the token is surfaced once, at
 * creation, in {@link CreateInviteResponse}.
 */
export interface InviteLink {
  id: string;
  role: Role;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
}

/** GET /account/invites — active invite links for the caller's account. */
export interface InvitesResponse {
  count: number;
  invites: InviteLink[];
  assignableRoles: Role[];
}

/** POST /account/invites — the just-minted link. `token`/`url` are shown exactly once. */
export interface CreateInviteResponse {
  status: "created";
  token: string;
  /** Ready-to-share URL when the server has an admin web origin configured; else absent. */
  url?: string;
  invite: InviteLink;
}

/** GET /entitlement — the resolved gate (read-only billing readout). */
export interface EntitlementGate {
  entitled: boolean;
  tier: Tier;
  palette: boolean;
  runSkills: boolean;
  authoring: boolean;
}

/** Current signed-installer metadata (TRE-1713, GET /releases/current). */
export interface ReleaseInfo {
  version: string;
  file: string;
  /** Lowercase 64-char hex SHA-256 — verify the download against this. */
  sha256: string;
  sizeBytes: number;
  releasedAt?: string;
  notes?: string;
  downloadPath: string;
}

export interface TrashedSkill {
  skillId: string;
  trashedBy: string;
  trashedAt: string;
}

export interface TrashResponse {
  count: number;
  skills: TrashedSkill[];
}

export interface Lock {
  skillId: string;
  holder: string;
  acquiredAt: string;
}

export interface LocksResponse {
  count: number;
  locks: Lock[];
}

/** GET /auth/config — which sign-in flows the server offers. */
export interface AuthConfig {
  ssoEnabled: boolean;
  /** Google IdP configured (TRE-830). The Google login route accepts the admin web
   *  origin as of TRE-838, so this drives the Google button directly. False (and the
   *  button disabled) until an OAuth client is registered — fail-safe. */
  googleEnabled: boolean;
  emailFallback: boolean;
}

/** POST /auth/email/verify success body. */
export interface EmailVerifyResponse {
  session: string;
  identity: Identity;
  expiresIn: number;
}

// --- Dev-handoff read surface (TRE-987, TRE-980) ----------------------------
// Mirrors src/skill-service/src/handoffs/supabase-read.ts. Snake_case fields are
// PostgREST column names passed straight through the read API.

/** The triage lifecycle (dev_handoffs.triage_status CHECK vocabulary). */
export const TRIAGE_STATUSES = ["new", "triaged", "linked", "dismissed"] as const;
export type TriageStatus = (typeof TRIAGE_STATUSES)[number];

/** The trigger classes that fire the loop (dev_handoffs.trigger_signal_class). */
export const TRIGGER_SIGNAL_CLASSES = [
  "hard_error",
  "capability_gap",
  "refusal",
  "explicit_wrong",
  "manual",
] as const;
export type TriggerSignalClass = (typeof TRIGGER_SIGNAL_CLASSES)[number];

/** One extracted Decision-summary row (a dev_handoff_gaps record). */
export interface HandoffGap {
  gap_id: string;
  handoff_id: string;
  ordinal: number | null;
  gap_summary: string | null;
  layer: string | null; // add-in-1a | server-1b | skill-2 | client-3 | mixed
  layer_raw: string | null;
  gap_type: string | null; // deploy | promote-to-tool | new
  proposal: string | null;
  owner: string | null;
  definition_of_done: string | null;
  backlog_issue_key: string | null;
}

/** A queue row (GET /admin/handoffs). Omits the heavy report_markdown blob. */
export interface HandoffSummary {
  handoff_id: string;
  created_at: string;
  received_at: string;
  session_id: string | null;
  correlation_id: string | null;
  subject: string | null;
  trigger_signal_class: string | null;
  trigger_fingerprint: string | null;
  cluster_key: string | null;
  scrub_status: string;
  revit_version: string | null;
  bridge_version: string | null;
  connector_version: string | null;
  tenant_id: string | null;
  plan_tier: string | null;
  triage_status: string;
  backlog_issue_key: string | null;
  gap_count: number;
}

/** The full report (GET /admin/handoffs/:id). */
export interface HandoffDetail extends HandoffSummary {
  report_markdown: string;
  report_sha256: string;
  scrub_findings: unknown;
  model_titles_sanitized: boolean | null;
  user_hash: string | null;
  gaps: HandoffGap[];
}

/** GET /admin/handoffs — the filterable triage queue. */
export interface HandoffListResponse {
  handoffs: HandoffSummary[];
  total: number | null;
  limit: number;
  offset: number;
}

export interface HandoffFilters {
  triageStatus?: string;
  triggerSignalClass?: string;
  clusterKey?: string;
  limit?: number;
  offset?: number;
}
